import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgModule, Injectable, ErrorHandler, Inject, InjectionToken } from '@angular/core';
import { AppViewerComponent } from './app-viewer.component';
import { AppViewerRoutingModule } from './app-viewer-routing.module';
import { NgxsModule } from '@ngxs/store';
import { LayoutModule } from '../../layout/layout.module';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { BookmarksModule } from '../../bookmarks/bookmarks.module';
import { EngineModule } from '../../engine/engine.module';
import { StoryModule } from '../../story/story.module';
import { OrganizationsModule } from '../../organizations/organizations.module';

import { LocaleConfigModule } from '../../locale-config.module';
import { DialogModule } from '../../dialogs/dialog.module';
import { ViewModule } from 'src/app/view/view.module';
import { ApiInterceptor } from 'src/app/interceptors/api.interceptor';
import { WidgetAuthModule } from 'src/app/widget-auth/widget-auth.module';
import { WidgetModule } from 'src/app/widget/widget.module';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { environment } from 'src/environments/environment';
import { UrlSerializer } from '@angular/router';
import { CustomUrlSerializer } from 'src/app/shared/libraries/url/custom-url-serializer';
import { BreakpointModule } from 'src/app/breakpoint/breakpoint.module';
import { UndoModule } from 'src/app/undo/undo.module';
import { MarkdownConfigModule } from 'src/app/markdown-config.module';
import { NgxGoogleAnalyticsModule } from 'ngx-google-analytics';
import { APP_BASE_HREF, LocationStrategy, HashLocationStrategy } from '@angular/common';
import { PendoModule } from 'src/app/pendo.module';
import { EnvironmentApplication } from 'src/environments/models/environment-application';
import { EnvironmentDeploy } from 'src/environments/models/environment-deploy';

declare const immer;
// enables es5 support, patches, and patchable Maps and Sets
immer.enableAllPlugins();
immer.enableES5();

// hash location strategy is needed to support offline use
const offlineProviders = [
  { provide: HTTP_INTERCEPTORS, useClass: ApiInterceptor, multi: true },
  { provide: UrlSerializer, useClass: CustomUrlSerializer },
  { provide: APP_BASE_HREF, useValue: './' },
  { provide: LocationStrategy, useClass: HashLocationStrategy },
];

const  onlineProviders = [  
    { provide: HTTP_INTERCEPTORS, useClass: ApiInterceptor, multi: true },
    { provide: UrlSerializer, useClass: CustomUrlSerializer },
  ];

@NgModule({
  declarations: [
    AppViewerComponent
  ],

  imports: [
    // This has to be at the top due to a bug in NgxsOnInit (https://github.com/ngxs/store/issues/539)
    NgxsModule.forRoot(),

    BreakpointModule,
    
    // NgxsReduxDevtoolsPluginModule.forRoot({
    //   disabled: environment.production
    // }),
    // Locales
    LocaleConfigModule.forRoot(),

    // Google Analytics
    NgxGoogleAnalyticsModule.forRoot(environment.googleAnalyticsId),

    // Pendo
    PendoModule.forRoot({
      enabled: true,
      pendoId: environment.pendoId,
      
      whitelist: {
        [EnvironmentApplication.Viewer]: (environment.deploy === EnvironmentDeploy.Production) ? 
          ['48Ab', '3FZO', '3FN2', '3Hb8', '3FQJ', '3FQU', '38fD', '3wgE', '3gLk', '3vSx', '3XdS', '3SOp', '3Um8', '3hOe', '3QWH', '4C1Q', '3WvE', '3epq', '3Dyq', '3O47', '3DJ4', '3fKF', '3gLf', '3PVx', '3caY', '3cTn', '3tVK', '3Yau', '3QSx', '3ZY5', '3Z6w', '3IF5', '3fKs', '45y8', '3fLN', '3QpZ', '3J6C', '3adO', '3Z6v', '3hjc', '3a50', '4EBE', '44FH', '3WlU', '3atu', '4E2N', '4Bma', '43bE', '3xxi', '3CT7', '4D5X', '3UVi', '3T7X', '3QT8', '3NG8', '3gyj', '3UWt', '3UWc'] :
          [
            'production/maleAdult/coronavirus_protein_structure',
            'production/maleAdult/eyeAnatomyTour',
            'production/maleAdult/acute_lymphoblastic_leukemia',
            'production/maleAdult/abdominal_aortic_aneurysm'
          ]
      },
      
      idPrefixes: {
        [EnvironmentDeploy.Local]: 'local-',
        [EnvironmentDeploy.QA]: 'qa-',
        [EnvironmentDeploy.Staging]: 'staging-',
        [EnvironmentDeploy.Production]: ''
      }
    }),


    // Markdown Support
    MarkdownConfigModule,

    // NoopAnimationsModule, // Disable animations for faster UI
    BrowserModule,
    BrowserAnimationsModule,
    HttpClientModule,

    // App dependencies
    EngineModule,

    // Child features
    BookmarksModule,
    LayoutModule,
    OrganizationsModule,
    StoryModule,

    UndoModule,


    // Dialogs
    DialogModule,

    ViewModule,

    // Widget auth + analytics
    WidgetModule,
    WidgetAuthModule,

    // App routing
    AppViewerRoutingModule
  ],
  providers: (environment.offline === true) ? offlineProviders : onlineProviders,
  bootstrap: [AppViewerComponent]
})
export class AppWidgetModule { }
